import { batch, createSignal, untrack } from "solid-js"
import type { EventWatchChange } from "@/transport/global-events"

/** A chat the page holds, with the event through which it holds all of the chat's events. */
export type WatchedSession = { sessionID: string; since: () => string | undefined }
/** A watched chat that may have missed events after `since`, or at any point without one. */
export type WatchGap = { sessionID: string; since?: string }

/**
 * Keeps an event stream watching the chats the page holds: the server sends
 * transcript events only for those (see the backend's events.Broker.Watch).
 * A chat counts as watched once the stream confirms it, after replaying what
 * the chat published since the position the page sent. Changes go one
 * request at a time, so the server applies them in order.
 */
export function createEventStreamWatch(input: {
  request(stream: string, change: EventWatchChange, signal: AbortSignal): Promise<boolean>
  /** Replaces the stream, which can no longer follow the page's chats. */
  reconnect(signal: AbortSignal): void
  /** Applies the events received so far, which a confirmation follows. */
  drain(): void
  token?: () => string
}) {
  let wanted = new Map<string, () => string | undefined>()
  // The position through which a chat was watched on a stream that ended.
  const carried = new Map<string, string | undefined>()
  const [confirmed, setConfirmed] = createSignal<ReadonlySet<string>>(new Set())
  let stream: { name: string; signal: AbortSignal } | undefined
  // What the open stream was asked to watch, confirmed or not.
  const sent = new Set<string>()
  const pending = new Map<string, string[]>()
  let requesting = false
  const listeners = new Set<(gaps: WatchGap[]) => void>()
  const token = input.token ?? (() => crypto.randomUUID())

  const sync = () => {
    const current = stream
    if (!current || requesting || current.signal.aborted) return
    const add = [...wanted.keys()].filter((sessionID) => !sent.has(sessionID))
    const remove = [...sent].filter((sessionID) => !wanted.has(sessionID))
    if (add.length === 0 && remove.length === 0) return
    const change: EventWatchChange = {
      token: token(),
      add: add.map((session) => carried.has(session)
        ? { session, since: carried.get(session), resume: true }
        : { session, since: wanted.get(session)?.() }),
      remove,
    }
    for (const sessionID of add) sent.add(sessionID)
    for (const sessionID of remove) sent.delete(sessionID)
    pending.set(change.token, add)
    requesting = true
    input.request(current.name, change, current.signal).then((open) => {
      if (!open && stream === current) input.reconnect(current.signal)
    }, (error: unknown) => {
      if (current.signal.aborted || stream !== current) return
      console.warn("[event-stream] could not change the chats the stream follows; reconnecting", error)
      input.reconnect(current.signal)
    }).finally(() => {
      requesting = false
      sync()
    })
  }

  return {
    /** Whether the stream delivers every transcript event of the chat from now on. */
    watching: (sessionID: string) => confirmed().has(sessionID),
    set(sessions: readonly WatchedSession[]) {
      wanted = new Map(sessions.map((session) => [session.sessionID, session.since]))
      for (const sessionID of carried.keys()) if (!wanted.has(sessionID)) carried.delete(sessionID)
      const current = untrack(confirmed)
      if ([...current].some((sessionID) => !wanted.has(sessionID))) {
        setConfirmed(new Set([...current].filter((sessionID) => wanted.has(sessionID))))
      }
      sync()
    },
    onGaps(listener: (gaps: WatchGap[]) => void) {
      listeners.add(listener)
      return () => void listeners.delete(listener)
    },
    /** A stream connected and named itself. */
    opened(name: string, signal: AbortSignal) {
      stream = { name, signal }
      sent.clear()
      pending.clear()
      sync()
    },
    /** The stream confirmed a change: its replay precedes this marker. */
    received(confirmation: string, gaps: Readonly<Record<string, string>>) {
      const added = pending.get(confirmation)
      if (!added) return
      pending.delete(confirmation)
      input.drain()
      const next = new Set(untrack(confirmed))
      for (const sessionID of added) {
        if (!sent.has(sessionID)) continue
        next.add(sessionID)
        carried.delete(sessionID)
      }
      const reported = Object.entries(gaps)
        .filter(([sessionID]) => next.has(sessionID))
        .map(([sessionID, since]) => ({ sessionID, since: since || undefined }))
      batch(() => {
        setConfirmed(next)
        if (reported.length) for (const listener of listeners) listener(reported)
      })
    },
    /**
     * The stream ended. `cursor` is the last event the page applied from it,
     * through which it holds every event of the chats it watched.
     */
    closed(cursor: string | undefined) {
      if (!stream) return
      for (const sessionID of untrack(confirmed)) carried.set(sessionID, cursor)
      stream = undefined
      sent.clear()
      pending.clear()
      setConfirmed(new Set<string>())
    },
    dispose() {
      listeners.clear()
      stream = undefined
    },
  }
}
